import { useState, useEffect, useCallback } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Crown, LogOut, Menu, X, Sun, Moon } from "lucide-react";

const LOGO_MEN =
  "https://customer-assets.emergentagent.com/job_491658ab-5b7f-4e73-96ad-15d40bd32385/artifacts/bl8jhxxf_file_00000000a58871f5bb9b7b728fcb5be0.png";
const LOGO_WOMEN =
  "https://customer-assets.emergentagent.com/job_docx-to-class/artifacts/ffe2ftra_file_000000000890720cac832b1f0894c497.png";

export default function Navbar() {
  const { user, logout, refresh, effectiveProgram } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState("light");

  const isWomen = effectiveProgram === "women";
  const LOGO_URL = isWomen ? LOGO_WOMEN : LOGO_MEN;

  // Sync local toggle state from body attribute (set by AuthContext / pre-paint script)
  useEffect(() => {
    const current = document.body.dataset.theme || "light";
    setTheme(current);
  }, [user]);

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const toggleTheme = useCallback(async () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    document.body.dataset.theme = next;
    document.documentElement.setAttribute("data-theme", next);
    try {
      window.localStorage.setItem("tcpi-theme", next);
    } catch (storageErr) {
      console.warn("tcpi-theme persistence unavailable:", storageErr?.message);
    }
    if (user) {
      try {
        await api.put("/me/settings", { theme: next });
        await refresh();
      } catch (err) {
        // Non-blocking — local theme already applied; server sync best-effort.
        console.warn("Theme sync to server failed:", err?.message);
      }
    }
  }, [theme, user, refresh]);

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${
      isActive ? "text-[#C9A961]" : "text-[#D1CFC9] hover:text-[#C9A961]"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `block px-5 py-4 text-lg border-b border-[#C9A961]/15 transition-colors ${
      isActive ? "text-[#C9A961] bg-[#C9A961]/10" : "text-[#F7F5F0] hover:text-[#C9A961] hover:bg-[#C9A961]/5"
    }`;

  const publicLinks = [
    { to: "/", label: "Home", end: true, testid: "mnav-home" },
    { to: "/about", label: "About", testid: "mnav-about" },
    { to: "/founder", label: "Founder", testid: "mnav-founder" },
    { to: "/fellowship", label: "Fellowship", testid: "mnav-fellowship" },
    { to: "/support", label: "Support", testid: "mnav-support" },
  ];

  const authedLinks = [
    { to: "/dashboard", label: "Dashboard", testid: "mnav-dashboard" },
    { to: "/tracks", label: "Tracks", testid: "mnav-tracks" },
    { to: "/forum", label: "Forum", testid: "mnav-forum" },
    { to: "/wallet", label: "Wallet", testid: "mnav-wallet" },
    { to: "/certificate", label: "Certificate", testid: "mnav-certificate" },
    { to: "/account/security", label: "Account & Security", testid: "mnav-account-security" },
  ];

  const adminLinks = [
    { to: "/facilitator", label: "Participants", testid: "mnav-facilitator" },
    { to: "/admin/subscribers", label: "Email List", testid: "mnav-admin-subscribers" },
  ];

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0908]/80 border-b border-[#C9A961]/15"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 md:py-4">
        <div className="flex items-center justify-between gap-2">
          <NavLogo logoUrl={LOGO_URL} isWomen={isWomen} />
          <DesktopNav user={user} linkClass={linkClass} />

          <div className="hidden md:flex items-center gap-3">
            <ThemeToggleButton theme={theme} onToggle={toggleTheme} testid="theme-toggle-desktop" compact />
            {user ? (
              <DesktopUserActions user={user} onLogout={handleLogout} />
            ) : (
              <DesktopGuestActions />
            )}
            <HamburgerButton
              open={mobileOpen}
              onToggle={() => setMobileOpen((v) => !v)}
              testid="hamburger-toggle-desktop"
              label="Menu"
            />
          </div>

          <MobileTopActions user={user} />
        </div>

        <div className="md:hidden mt-3">
          <HamburgerButton
            open={mobileOpen}
            onToggle={() => setMobileOpen((v) => !v)}
            testid="hamburger-toggle"
            label={mobileOpen ? "Close menu" : "Menu — see all areas"}
            fullWidth
          />
        </div>
      </div>

      {mobileOpen && (
        <MobileDrawer
          user={user}
          publicLinks={publicLinks}
          authedLinks={authedLinks}
          adminLinks={adminLinks}
          mobileLinkClass={mobileLinkClass}
          theme={theme}
          onToggleTheme={toggleTheme}
          onLogout={handleLogout}
          onDismiss={() => setMobileOpen(false)}
        />
      )}
    </header>
  );
}

/* ---------- Sub-components ---------- */

function NavLogo({ logoUrl, isWomen }) {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
      <img src={logoUrl} alt="TCPI" className="h-10 w-10 object-contain" />
      <div className="hidden sm:block">
        <div
          className="text-[#C9A961] font-serif text-lg leading-none"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          The Crown Path
        </div>
        <div className="overline text-[10px] mt-1">
          {isWomen ? "For Women & Young Girls" : "Initiative"}
        </div>
      </div>
    </Link>
  );
}

function DesktopNav({ user, linkClass }) {
  const isStaff = user && (user.role === "facilitator" || user.role === "admin");
  return (
    <nav className="hidden md:flex items-center gap-8">
      <NavLink to="/" end className={linkClass} data-testid="nav-home">Home</NavLink>
      <NavLink to="/about" className={linkClass} data-testid="nav-about">About</NavLink>
      <NavLink to="/founder" className={linkClass} data-testid="nav-founder">Founder</NavLink>
      <NavLink to="/fellowship" className={linkClass} data-testid="nav-fellowship">Fellowship</NavLink>
      <NavLink to="/support" className={linkClass} data-testid="nav-support">Support</NavLink>
      {user && (
        <>
          <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard">Dashboard</NavLink>
          <NavLink to="/tracks" className={linkClass} data-testid="nav-tracks">Tracks</NavLink>
          <NavLink to="/forum" className={linkClass} data-testid="nav-forum">Forum</NavLink>
          <NavLink to="/wallet" className={linkClass} data-testid="nav-wallet">Wallet</NavLink>
          {isStaff && (
            <>
              <NavLink to="/facilitator" className={linkClass} data-testid="nav-facilitator">Participants</NavLink>
              <NavLink to="/admin/subscribers" className={linkClass} data-testid="nav-admin-subscribers">Email List</NavLink>
            </>
          )}
        </>
      )}
    </nav>
  );
}

function ThemeToggleButton({ theme, onToggle, testid, compact = false }) {
  const Icon = theme === "light" ? Moon : Sun;
  if (compact) {
    return (
      <button
        onClick={onToggle}
        className="inline-flex p-2 rounded-full border border-[#C9A961]/25 text-[#C9A961] hover:bg-[#C9A961]/10 transition"
        aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
        data-testid={testid}
      >
        <Icon className="w-4 h-4" />
      </button>
    );
  }
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 rounded-md border border-[#C9A961]/30 text-[#C9A961] hover:bg-[#C9A961]/10 transition"
      data-testid={testid}
    >
      <span className="text-sm">
        {theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
      </span>
      <Icon className="w-4 h-4" />
    </button>
  );
}

function DesktopGuestActions() {
  return (
    <>
      <Link to="/login" className="text-sm text-[#D1CFC9] hover:text-[#C9A961]" data-testid="nav-login">
        Sign in
      </Link>
      <Link to="/register" className="btn-primary text-sm" data-testid="nav-register">
        Enroll
      </Link>
    </>
  );
}

function DesktopUserActions({ user, onLogout }) {
  return (
    <>
      <Link to="/certificate" className="flex items-center gap-2 text-sm text-[#C9A961]" data-testid="nav-certificate">
        <Crown className="w-4 h-4" />
        {user.name?.split(" ")[0] || "You"}
      </Link>
      <button
        onClick={onLogout}
        className="text-sm text-[#D1CFC9] hover:text-[#C9A961] flex items-center gap-2"
        data-testid="nav-logout"
      >
        <LogOut className="w-4 h-4" /> Sign out
      </button>
    </>
  );
}

function MobileTopActions({ user }) {
  return (
    <div className="flex md:hidden items-center gap-2">
      {user ? (
        <Link to="/certificate" className="flex items-center gap-1 text-sm text-[#C9A961]" data-testid="nav-certificate-mobile">
          <Crown className="w-4 h-4" />
          <span className="text-xs">{user.name?.split(" ")[0] || "You"}</span>
        </Link>
      ) : (
        <>
          <Link to="/login" className="text-sm text-[#D1CFC9] hover:text-[#C9A961] px-2" data-testid="nav-login-mobile">
            Sign in
          </Link>
          <Link to="/register" className="btn-primary text-xs px-3 py-2" data-testid="nav-register-mobile">
            Enroll
          </Link>
        </>
      )}
    </div>
  );
}

function HamburgerButton({ open, onToggle, testid, label, fullWidth = false }) {
  const Icon = open ? X : Menu;
  const widthClass = fullWidth ? "w-full justify-center" : "";
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-2 px-3 py-2 rounded-md bg-[#C9A961] text-[#0A0908] font-semibold hover:bg-[#f4d03f] transition shadow-md ${widthClass}`}
      aria-label="Open menu"
      aria-expanded={open}
      data-testid={testid}
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm tracking-wide">{label}</span>
    </button>
  );
}

function MobileDrawer({
  user, publicLinks, authedLinks, adminLinks, mobileLinkClass,
  theme, onToggleTheme, onLogout, onDismiss,
}) {
  const isStaff = user && (user.role === "facilitator" || user.role === "admin");
  return (
    <>
      <div
        className="fixed inset-0 top-[140px] md:top-[73px] z-20 bg-[#0A0908]/60 backdrop-blur-sm hidden md:block"
        onClick={onDismiss}
        aria-hidden="true"
      />
      <div
        className="fixed inset-x-0 md:inset-x-auto md:right-0 top-[140px] md:top-[73px] md:bottom-auto md:w-96 z-30 bg-[#0A0908] md:bg-[#121110] md:border-l md:border-[#C9A961]/20 md:shadow-2xl overflow-y-auto h-[calc(100vh-140px)] md:h-auto md:max-h-[80vh]"
        data-testid="mobile-menu"
      >
        <div className="px-2 pb-32 md:pb-12">
          <DrawerSection title="Explore">
            {publicLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={mobileLinkClass} data-testid={l.testid}>
                {l.label}
              </NavLink>
            ))}
          </DrawerSection>

          {user ? (
            <>
              <DrawerSection title="Your Path">
                {authedLinks.map((l) => (
                  <NavLink key={l.to} to={l.to} className={mobileLinkClass} data-testid={l.testid}>
                    {l.label}
                  </NavLink>
                ))}
              </DrawerSection>
              {isStaff && (
                <DrawerSection title="Manage">
                  {adminLinks.map((l) => (
                    <NavLink key={l.to} to={l.to} className={mobileLinkClass} data-testid={l.testid}>
                      {l.label}
                    </NavLink>
                  ))}
                </DrawerSection>
              )}
            </>
          ) : (
            <div className="px-5 pt-6 flex flex-col gap-3">
              <Link to="/login" className="btn-secondary text-center" data-testid="mnav-login">Sign in</Link>
              <Link to="/register" className="btn-primary text-center" data-testid="mnav-register">Enroll</Link>
            </div>
          )}

          <div className="px-5 pt-6 border-t border-[#C9A961]/15 mt-6 space-y-3">
            <ThemeToggleButton theme={theme} onToggle={onToggleTheme} testid="theme-toggle-mobile" />
            {user && (
              <button
                onClick={onLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-[#C9A961]/30 text-[#D1CFC9] hover:text-[#C9A961] hover:bg-[#C9A961]/5 transition"
                data-testid="mnav-logout"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function DrawerSection({ title, children }) {
  return (
    <>
      <div className="overline px-5 pt-6 pb-2 text-xs">{title}</div>
      {children}
    </>
  );
}
